import * as React from "react";
import * as moment from "moment";
import {Document, ICustomerPortalService, Party, Policy, Subscription, Types, POLICY_STATUS_MTC, SESSION_STORAGE_KEY_CLAIM_POLICY} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {resolveFuelType, resolveTransmission} from "../../../../../utils/resolveLookupValues";
import {DocumentsPanel} from "./DocumentsPanel";
import {mapDocumentTitles} from "../../../../../utils/DataMappers";
import {getAddressLines, getAmountToDisplay, setSessionStorage} from "../../../../../CommonFunctions";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {Link} from "react-router-dom";
import {getInteractionVehicleFuelType} from "@falcon/plugin-falcon-journey";
import {AdditionalPolicyholderDetailsWidget} from "./AdditionalPolicyholderDetailsWidget";

interface IProps {
    policy: Policy;
    // Policy that is still active but is going to be replaced
    replacedPolicy: Policy | undefined;
    subscription: Subscription;
    vehicleIndex: number;
    party: Party;
}

interface IState {
    documents?: Document[];
}

export class YourCarCoverPanelPlain extends React.Component<IProps & IWithRouterProps, IState> {

    private readonly policyToRender: Policy;
    private readonly vehicleToRender: any;
    private readonly cancelled: boolean;
    private readonly vehicleDrivers: any[] = [];
    private readonly renewalPaymentFailed: boolean;
    private readonly replaced: boolean;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps & IWithRouterProps) {
        super(props);

        this.state = {};

        this.policyToRender = this.props.replacedPolicy || this.props.policy;
        this.vehicleToRender = this.policyToRender.interaction.vehicles[0];
        this.cancelled = this.props.policy.status === POLICY_STATUS_MTC;
        this.renewalPaymentFailed = this.props.subscription.renewalPaymentFailed;
        this.replaced = this.props.replacedPolicy !== undefined ? true : false;

        const allDrivers: any[] = [this.policyToRender.interaction.policyHolder].concat(this.policyToRender.interaction.additionalDrivers || []);
        if (this.vehicleToRender.driverIds) {
            this.vehicleDrivers = this.vehicleToRender.driverIds
                .map((driverId: string) => allDrivers.find((driver: any) => driverId === driver.personId))
                .filter((driver: any) => driver !== undefined);
        }
    }

    public componentDidMount(): void {
        this.customerPortalService.getPolicyDocumentsByPolicyVersionId(this.policyToRender.policyNumber, this.policyToRender.interaction.agreementVersionId).subscribe(
            (documents: Document[]) => {
                this.setState({
                    documents: mapDocumentTitles(documents, this.policyToRender)
                });
            }
        );
    }

    public render() {
        const {documents} = this.state;
        const {vehicleIndex} = this.props;

        return (
            <React.Fragment>
                <Overlay show={!documents}/>
                <div className="section-panel your-cover-panel">
                    <img className="main-icon" src="/img/Flow-car-cover.svg" alt="Car cover icon"/>
                    {this.cancelled && this.renderCancellationDisclaimer()}
                    <div className="section">
                        <div className="section-heading">Cover details</div>
                        <div className="section-contents">
                            {this.renderCoverDetails(vehicleIndex)}
                        </div>
                    </div>
                    <div className="section">
                        <div className="section-heading">Car details</div>
                        <div className="section-contents">
                            {this.renderCarDetails()}
                        </div>
                    </div>
                    {this.renderDriverDetailSections()}
                    <AdditionalPolicyholderDetailsWidget properties={this.props.party.customProperties}
                        coverCancelled={this.props.subscription.allPoliciesAreCancelled}
                        unpaidRenewal={this.props.subscription.renewalPaymentFailed}
                        interactionId={this.policyToRender.interaction._id}
                    />
                    <div className="section">
                        <div className="section-heading">Policy documents</div>
                        <div className="section-contents documents">
                            <DocumentsPanel documents={this.state.documents}/>
                        </div>
                    </div>
                    <div className="section">
                        <div className="section-links">
                            <button type="button"
                                    className={`btn btn-primary btn-next plugin buttonLink`}
                                    onClick={this.redirectToClaim}>
                                Make a claim
                            </button>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private renderCancellationDisclaimer(): React.ReactNode {
        const endDate: moment.Moment = moment.utc(this.props.policy.endDate, moment.ISO_8601);

        let disclaimer: string;
        if (endDate.isSame(moment(), 'day')) {
            disclaimer = `Cover ends today`;
        } else if (endDate.isBefore(moment(), 'day')) {
            disclaimer = `Cover ended on ${endDate.format("D MMMM YYYY")}`;
        } else {
            disclaimer = `Cover will end on ${endDate.format("D MMMM YYYY")}`;
        }

        return (
            <div id={`carCancellationDisclaimer${this.props.vehicleIndex}`} className="warning">
                {disclaimer}
            </div>
        );
    }

    private renderCoverDetails(vehicleIndex: number): React.ReactNode {
        const {policy, subscription} = this.props;
        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-key font-weight-extra-light">
                        Last subscription amount paid
                    </div>
                    <div className="detail-value price">{`${getAmountToDisplay(subscription.lastPaidAmountPerPolicy[policy.policyNumber as keyof Map<string, number>])}`}</div>
                </div>
                {!this.cancelled &&
                    <div className="detail">
                        <div className="detail-key font-weight-extra-light">Next payment date</div>
                        <div className="detail-value font-weight-semi-bold">{moment(subscription.nextPaymentDate, moment.ISO_8601).format("DD MMMM YYYY")}</div>
                        <div className="detail-value">(Payment will be calculated on {moment(subscription.renewalWindowStartDate, moment.ISO_8601).format("DD MMMM")})</div>
                    </div>
                }
                <div className="detail">
                    <div className="detail-key">Cover start</div>
                    <div className="detail-value" id={`vehicle${vehicleIndex}-startDate`}>{moment.utc(policy.startDate, moment.ISO_8601).format("DD/MM/YYYY")}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Cover end</div>
                    <div className="detail-value" id={`vehicle${vehicleIndex}-endDate`}>{this.cancelled ? moment.utc(policy.endDate, moment.ISO_8601).format("DD/MM/YYYY") : "Anytime"}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Guaranteed hire car</div>
                    <div className="detail-value">{this.vehicleToRender.extraProtection.courtesyCar ? "Included" : "Not Included"}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Motor legal expenses</div>
                    <div className="detail-value">{this.vehicleToRender.extraProtection.legalProtection ? "Included" : "Not Included"}</div>
                </div>
                {this.renderMtaLink()}
            </React.Fragment>
        );
    }

    private renderCarDetails(): React.ReactNode {
        const vehicleFuelTypeActual = getInteractionVehicleFuelType(this.vehicleToRender.fuelType, this.vehicleToRender.fuelTypeFull);
        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-value">{`${this.vehicleToRender.make}, ${this.vehicleToRender.model} (${this.vehicleToRender.registrationNumber})`}</div>
                    <div className="detail-value">{`${this.vehicleToRender.year}, ${resolveTransmission(this.vehicleToRender.transmission)}, ${resolveFuelType(vehicleFuelTypeActual)}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Car usage</div>
                    <div className="detail-value">
                        {this.vehicleToRender.businessUse ? "Business use excluding commercial travelling" :
                            "Social domestic and pleasure including commuting to one place to work"}
                    </div>
                </div>
                <div className="detail">
                    <div className="detail-key">Voluntary excess</div>
                    <div className="detail-value">{`£${this.vehicleToRender.excesses.voluntaryExcess}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Drivers</div>
                    {this.renderVehicleDrivers()}
                </div>
                {this.renderMtaLink()}
            </React.Fragment>
        );
    }

    private renderVehicleDrivers(): React.ReactNode[] {
        return this.vehicleDrivers.map((driver: any, index: number) => {
            return (
                <div key={index} className="detail-value">
                    {`${driver.firstName} ${driver.lastName}`} {driver.personId === this.vehicleToRender.mainDriverId && <span className="font-weight-bold">(main driver)</span>}
                </div>
            );
        });
    }

    private renderDriverDetailSections(): React.ReactNode[] {
        return this.vehicleDrivers.map((driver: any, index: number) => (
            <div key={index} className="section">
                <div className="section-heading">Driver details</div>
                <div className="section-contents">
                    {this.renderDriverDetails(driver)}
                </div>
            </div>
        ));
    }

    private renderDriverDetails(driver: any): React.ReactNode {
        const isPolicyHolder: boolean = driver.personId === this.policyToRender.interaction.policyHolder.personId;

        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-value">{`${driver.firstName} ${driver.lastName}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Date of birth</div>
                    <div className="detail-value">{moment(driver.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}</div>
                </div>
                {driver.dlnLookupData?.dlnLookupCallSucess && <div className="detail">
                    <div className="detail-key">Driving licence number</div>
                    <div className="detail-value">{driver.licence.number}</div>
                </div>}
                {isPolicyHolder && this.renderPolicyHolderDetails()}
            </React.Fragment>
        );
    }

    private renderPolicyHolderDetails(): React.ReactNode {
        const party = this.policyToRender.policyHolder;
        const {policyHolder} = this.policyToRender.interaction;
        const addressComponents: string[] = getAddressLines(policyHolder.address);
        const addressComponentsToRender: React.ReactNode[] = addressComponents.map(
            (component: string, index: number) => index === 0 ?
                <span key={index} className="detail-value">{component}</span> :
                <React.Fragment><span key={index} className="detail-value">, {component}</span></React.Fragment>
        );

        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-key">Address</div>
                    {addressComponentsToRender}
                </div>
                <div className="detail">
                    <div className="detail-key">Email</div>
                    <div className="detail-value">
                        {party.email}
                    </div>
                </div>
                <div className="link-edit">
                    <Link to={`/journey/show/product/MotorInsurance/process/mtaChangePolicyHolderDrivingLicenseDetails/changePolicyHolderDrivingLicenseDetailsPresetPage/${this.props.policy.interaction._id}`}
                            aria-label="Edit licence details"
                            id="accountHolderScanDrivingLicense"
                            className={(this.cancelled || this.replaced || this.renewalPaymentFailed) ? "disabled" : ""}>
                        Edit
                    </Link>
                </div>
            </React.Fragment>
        );
    }

    private redirectToClaim = (event: any): void => {
        setSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY, this.props.policy);
        this.props.navigate("/dashboard/windscreen",{state: {prevPath: location.pathname}});
    };

    private renderMtaLink(): React.ReactNode {
        return (
            <div className="link-edit">
                <Link
                    id={`editCar${this.props.vehicleIndex}Details`}
                    aria-label="Edit car details"
                    to={`/journey/init/product/MotorInsurance/process/mta/car${this.props.vehicleIndex}MtaEntryPage/${this.props.policy.interaction._id}`}
                    className={(this.cancelled || this.replaced || this.renewalPaymentFailed) ? "disabled" : ""}
                >Edit</Link>
            </div>
        )
    }
}

export const YourCarCoverPanel = withRouter<IProps>(YourCarCoverPanelPlain)
